// apps/radar/src/lib/api/hooks.ts
// Version: 2026.4.24
// Thin async-data hooks over @forgetrack/api-client's useRadarApi. No global
// query cache yet — radar pages have light traffic and a useEffect-based
// fetcher keeps the dependency surface small. Swap to TanStack Query when
// the inbox shows real data and stale-while-revalidate matters.

import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@forgetrack/auth';
import {
  useRadarApi,
  type RadarLeadSummary,
  type RadarLeadDetail,
  type RadarSource,
  type RadarReviewItem,
  type RadarDashboardSummary,
  type RadarLeadListFilters,
} from '@forgetrack/api-client';

interface AsyncResource<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Generic resource hook that respects the auth-initialized state — fetches
 * fire only after MSAL has decided whether the user is signed in, and
 * surface an error if the call fails. The fetcher is captured per-render
 * via useCallback so changing dependencies trigger a refetch automatically.
 */
function useAsyncResource<T>(
  fetcher: () => Promise<T>,
  deps: React.DependencyList,
): AsyncResource<T> {
  const { isInitialized, isAuthenticated } = useAuth();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [version, setVersion] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableFetcher = useCallback(fetcher, deps);

  useEffect(() => {
    if (!isInitialized) return;
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    stableFetcher()
      .then(result => {
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [isInitialized, isAuthenticated, stableFetcher, version]);

  return { data, loading, error, refetch: () => setVersion(v => v + 1) };
}

export function useDashboard(): AsyncResource<RadarDashboardSummary> {
  const api = useRadarApi();
  return useAsyncResource(() => api.getDashboard(), [api]);
}

export function useLeads(filters: RadarLeadListFilters = {}): AsyncResource<RadarLeadSummary[]> {
  const api = useRadarApi();
  // Stringify filters once so the dep is stable across renders with the same shape.
  const key = JSON.stringify(filters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useAsyncResource(() => api.listLeads(filters), [api, key]);
}

export function useLead(leadId: string | undefined): AsyncResource<RadarLeadDetail> {
  const api = useRadarApi();
  return useAsyncResource(
    () => leadId ? api.getLead(leadId) : Promise.reject(new Error('No lead id')),
    [api, leadId],
  );
}

export function useSources(): AsyncResource<RadarSource[]> {
  const api = useRadarApi();
  return useAsyncResource(() => api.listSources(), [api]);
}

export function useReviewQueue(): AsyncResource<RadarReviewItem[]> {
  const api = useRadarApi();
  return useAsyncResource(() => api.listReviewQueue(), [api]);
}
